import type { FC, ReactNode } from 'react'

import { Download, Play } from 'lucide-react'
import { cn } from '@/lib/utils'

const ART_TILES = [
    (
        <svg key='mountains' viewBox='0 0 160 100' preserveAspectRatio='xMidYMid slice' className='h-full w-full'>
            <rect width='160' height='100' fill='#0d9488' />
            <circle cx='118' cy='28' r='11' fill='#ccfbf1' />
            <path d='M0 100 L48 44 L80 74 L108 50 L160 100 Z' fill='#5eead4' fillOpacity='0.85' />
        </svg>
    ),
    (
        <svg key='zigzag' viewBox='0 0 160 100' preserveAspectRatio='xMidYMid slice' className='h-full w-full'>
            <rect width='160' height='100' fill='#7c3aed' />
            <path d='M14 78 L42 26 L70 78 L98 26 L126 78 L148 40' stroke='#ddd6fe' strokeWidth='7' fill='none' strokeLinejoin='round' strokeLinecap='round' />
        </svg>
    ),
    (
        <svg key='waves' viewBox='0 0 160 100' preserveAspectRatio='xMidYMid slice' className='h-full w-full'>
            <rect width='160' height='100' fill='#065f46' />
            <path d='M0 46 C20 30 40 30 60 46 S100 62 120 46 S150 30 160 38' stroke='#6ee7b7' strokeWidth='6' fill='none' strokeLinecap='round' />
            <path d='M0 68 C20 52 40 52 60 68 S100 84 120 68 S150 52 160 60' stroke='#6ee7b7' strokeOpacity='0.5' strokeWidth='6' fill='none' strokeLinecap='round' />
        </svg>
    ),
    (
        <svg key='sun' viewBox='0 0 160 100' preserveAspectRatio='xMidYMid slice' className='h-full w-full'>
            <rect width='160' height='100' fill='#ea580c' />
            <circle cx='84' cy='48' r='24' fill='#fde68a' />
            <path d='M0 100 Q50 64 100 80 T160 72 V100 Z' fill='#9a3412' />
        </svg>
    )
]

const LOGO_SQUARES = ['bg-cyan-400', 'bg-violet-500', 'bg-amber-400', 'bg-emerald-500']

interface ExportFileProps {
    className?: string
}

// A stylised MP4 file: the exported composition, its name and a download button.
const ExportFile: FC<ExportFileProps> = ({ className }): ReactNode => {
    return (
        <div className={cn('relative mx-auto w-full max-w-[21rem]', className)}>
            <div className='@container relative aspect-[280/340]'>
                <span className='absolute inset-0 -translate-x-[7%] translate-y-[3%] -rotate-6 rounded-[8cqw] border border-indigo-300/60 bg-indigo-200/50 dark:border-indigo-400/20 dark:bg-indigo-500/10' />
                <span className='absolute inset-0 translate-x-[6%] translate-y-[2%] rotate-[5deg] rounded-[8cqw] border border-sky-300/60 bg-sky-200/40 dark:border-sky-400/20 dark:bg-sky-500/10' />

                <svg
                    viewBox='0 0 280 340'
                    aria-hidden='true'
                    className='absolute inset-0 h-full w-full overflow-visible drop-shadow-[0_24px_36px_rgb(37_99_235/0.2)]'
                >
                    <path
                        d='M24 0 H212 L280 68 V316 A24 24 0 0 1 256 340 H24 A24 24 0 0 1 0 316 V24 A24 24 0 0 1 24 0 Z'
                        className='fill-card stroke-border'
                        strokeWidth='1.5'
                    />
                    <path
                        d='M212 0 V48 A20 20 0 0 0 232 68 H280 Z'
                        className='fill-muted stroke-border'
                        strokeWidth='1.5'
                        strokeLinejoin='round'
                    />
                </svg>

                <div className='absolute inset-0 flex flex-col px-[9cqw] pb-[7cqw] pt-[9cqw]'>
                    <div className='flex items-center gap-[2.6cqw]'>
                        <span className='grid grid-cols-2 gap-[0.9cqw]'>
                            {LOGO_SQUARES.map((square) => (
                                <span key={square} className={`size-[2.8cqw] rounded-[0.7cqw] ${square}`} />
                            ))}
                        </span>

                        <span className='font-heading text-[4.6cqw] font-bold text-foreground'>
                            MultiScreen
                        </span>
                    </div>

                    <div className='relative mt-[5cqw] grid aspect-[16/10] grid-cols-2 grid-rows-2 gap-[1.6cqw] rounded-[3.4cqw] bg-muted p-[1.6cqw] ring-1 ring-border'>
                        {ART_TILES.map((tile, index) => (
                            <span key={index} className='overflow-hidden rounded-[2cqw]'>
                                {tile}
                            </span>
                        ))}

                        <span className='absolute left-1/2 top-1/2 grid size-[13cqw] -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full bg-slate-950 text-white shadow-lg ring-[1.2cqw] ring-card'>
                            <Play className='size-[5.5cqw] translate-x-[0.4cqw]' fill='currentColor' />
                        </span>
                    </div>

                    <p className='mt-[5cqw] text-[4.6cqw] text-muted-foreground'>
                        MultiScreen.mp4
                    </p>

                    <div className='mt-[1cqw] flex items-center justify-between'>
                        <span className='font-heading text-[12cqw] font-extrabold leading-none tracking-[-0.04em] text-foreground'>
                            .MP4
                        </span>

                        <span className='grid size-[14cqw] place-items-center rounded-[3.6cqw] bg-primary text-primary-foreground shadow-[0_10px_24px_-10px_rgb(37_99_235/0.8)]'>
                            <Download className='size-[7cqw]' />
                        </span>
                    </div>

                    <span className='mt-auto block h-px bg-border' />

                    <p className='mt-[4cqw] flex items-center gap-[2cqw] font-mono text-[3.4cqw] font-semibold uppercase tracking-[0.18em] text-muted-foreground'>
                        <span className='size-[2.2cqw] rounded-full bg-emerald-500' />
                        MP4 · Video
                    </p>
                </div>
            </div>

            <span className='mx-auto mt-[6%] block h-4 w-[70%] rounded-[50%] bg-slate-900/15 blur-md dark:bg-black/50' />
        </div>
    )
}

export default ExportFile
